import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, SkipBack, SkipForward, Clock, Calendar, ChevronRight, Mic, Bell } from 'lucide-react';
import { useHeroImage } from '../hooks/usePageHero';

// ── Design tokens ────────────────────────────────────────────────────────────
const SERIF  = "'Newsreader', Georgia, serif";
const SANS   = "'Public Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const NAVY   = '#16304a';
const TEAL   = '#1d6f6a';
const GOLD   = '#c79a3a';
const PAPER  = '#f6efe2';
const CREAM  = '#faf7f0';
const BORDER = '#e8ddd0';
const INK    = '#2a2e31';
const MUTED  = '#5b6066';
const WHITE  = '#ffffff';

// ── Static episode data ──────────────────────────────────────────────────────
const FEATURED = {
  season: 'S1 · E12',
  title:  'Preparing the Workforce for an AI-Powered Economy',
  guest:  'Dr. Alicia Thornton, Future of Work Institute',
  date:   'June 5, 2025',
};

const EPISODES = [
  {
    id: 1, num: 'E11', theme: 'Government',
    title: 'The Future of Work Policy in Washington',
    desc:  'Senator Mark Reynolds on bipartisan solutions for the modern workforce.',
    date:  'May 22, 2025',
  },
  {
    id: 2, num: 'E10', theme: 'Healthcare',
    title: 'Healthcare Access and Innovation',
    desc:  'Leaders discuss policy, technology, and the patient experience.',
    date:  'May 15, 2025',
  },
  {
    id: 3, num: 'E9', theme: 'Technology',
    title: 'AI, Ethics, and Responsible Innovation',
    desc:  'Building technology that serves people and communities.',
    date:  'May 8, 2025',
  },
  {
    id: 4, num: 'E8', theme: 'Workforce',
    title: 'Closing the Skills Gap Where It Matters Most',
    desc:  'Inside apprenticeship and training programs driving real results.',
    date:  'May 1, 2025',
  },
];

const THEMES = [
  'Leadership', 'Workforce', 'AI & Technology', 'Education',
  'Healthcare', 'Government', 'Business & Finance',
];

// ── Episode theme accent colors ───────────────────────────────────────────────
const THEME_BG = { Government: '#e8f0ee', Healthcare: '#edf2f0', Technology: '#e8eef6', Workforce: '#f5ede0' };
const THEME_IC = { Government: TEAL,       Healthcare: TEAL,       Technology: NAVY,       Workforce: GOLD  };

// ── Thematic mini SVG illustrations for episode cards ────────────────────────
function EpIllo({ theme }) {
  const bg = THEME_BG[theme] || '#f0ece4';
  if (theme === 'Government') return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      <rect width="80" height="56" rx="6" fill={bg}/>
      <rect x="20" y="38" width="40" height="12" rx="2" fill={TEAL} opacity="0.7"/>
      <polygon points="40,12 18,38 62,38" fill={TEAL} opacity="0.55"/>
      <rect x="37" y="8" width="6" height="8" fill={GOLD} opacity="0.8"/>
    </svg>
  );
  if (theme === 'Healthcare') return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      <rect width="80" height="56" rx="6" fill={bg}/>
      <rect x="34" y="18" width="12" height="20" rx="3" fill={TEAL} opacity="0.7"/>
      <rect x="26" y="26" width="28" height="8" rx="3" fill={TEAL} opacity="0.55"/>
      <circle cx="40" cy="28" r="14" fill="none" stroke={TEAL} strokeWidth="1.2" opacity="0.3"/>
    </svg>
  );
  if (theme === 'Technology') return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      <rect width="80" height="56" rx="6" fill={bg}/>
      <rect x="22" y="18" width="36" height="22" rx="3" fill={NAVY} opacity="0.12"/>
      <circle cx="32" cy="29" r="4" fill={NAVY} opacity="0.4"/>
      <circle cx="48" cy="29" r="4" fill={NAVY} opacity="0.4"/>
      <line x1="36" y1="29" x2="44" y2="29" stroke={TEAL} strokeWidth="1.5" opacity="0.7"/>
      <rect x="24" y="20" width="8" height="5" rx="1" fill={TEAL} opacity="0.5"/>
      <rect x="48" y="32" width="8" height="5" rx="1" fill={GOLD} opacity="0.6"/>
    </svg>
  );
  return (
    <svg viewBox="0 0 80 56" width="80" height="56" aria-hidden="true">
      <rect width="80" height="56" rx="6" fill={bg}/>
      <rect x="20" y="30" width="12" height="16" rx="2" fill={GOLD} opacity="0.7"/>
      <rect x="34" y="22" width="12" height="24" rx="2" fill={TEAL} opacity="0.65"/>
      <rect x="48" y="26" width="12" height="20" rx="2" fill={NAVY} opacity="0.4"/>
    </svg>
  );
}

// ── Hero studio SVG illustration ─────────────────────────────────────────────
function StudioIllo() {
  return (
    <svg viewBox="0 0 460 300" width="100%" style={{ maxWidth: 440, display: 'block' }} aria-hidden="true">
      {/* Studio bg */}
      <rect width="460" height="300" rx="14" fill="#f0e7d5"/>
      {/* Acoustic panels — back wall */}
      {[22, 88, 154, 220, 286, 352].map((x, i) => (
        <rect key={i} x={x} y="14" width="52" height="76" rx="6"
          fill={i % 2 === 0 ? '#e2d5c0' : '#e9ddc8'} opacity="0.85"/>
      ))}
      {/* Window highlight */}
      <rect x="310" y="18" width="126" height="70" rx="8" fill="#d4c9b2" opacity="0.6"/>
      <line x1="373" y1="18" x2="373" y2="88" stroke="#c8bda8" strokeWidth="1.2"/>
      <line x1="310" y1="53" x2="436" y2="53" stroke="#c8bda8" strokeWidth="1.2"/>
      {/* Monitor/screen */}
      <rect x="28" y="18" width="82" height="56" rx="6" fill={NAVY} opacity="0.82"/>
      <rect x="32" y="22" width="74" height="44" rx="4" fill="#0e2535"/>
      <text x="69" y="48" textAnchor="middle" fontFamily={SANS} fontWeight="800"
        fontSize="16" fill={WHITE} opacity="0.9">OP</text>
      <rect x="62" y="74" width="16" height="8" rx="2" fill={NAVY} opacity="0.6"/>
      {/* Round table */}
      <ellipse cx="230" cy="222" rx="165" ry="48" fill="#c8b49a"/>
      <ellipse cx="230" cy="208" rx="165" ry="48" fill="#d8c5ad"/>
      <ellipse cx="230" cy="204" rx="158" ry="42" fill="#e4d0b8"/>
      {/* Table highlight */}
      <ellipse cx="200" cy="198" rx="90" ry="18" fill={WHITE} opacity="0.12"/>
      {/* Microphone — center */}
      <rect x="226" y="118" width="8" height="80" rx="3" fill="#7a8590"/>
      <rect x="212" y="194" width="36" height="5" rx="2.5" fill="#7a8590"/>
      <ellipse cx="230" cy="100" rx="18" ry="24" fill="#2c3a44" stroke="#16304a" strokeWidth="1.5"/>
      <rect x="222" y="88" width="16" height="28" rx="6" fill="#1e2d38" opacity="0.9"/>
      <line x1="226" y1="96" x2="234" y2="96" stroke="#5a7a8a" strokeWidth="1.2" opacity="0.6"/>
      <line x1="225" y1="102" x2="235" y2="102" stroke="#5a7a8a" strokeWidth="1.2" opacity="0.6"/>
      <line x1="226" y1="108" x2="234" y2="108" stroke="#5a7a8a" strokeWidth="1.2" opacity="0.6"/>
      {/* Microphone — left */}
      <rect x="134" y="138" width="6" height="62" rx="2.5" fill="#8a9399" opacity="0.8"/>
      <rect x="124" y="196" width="26" height="4" rx="2" fill="#8a9399" opacity="0.8"/>
      <ellipse cx="137" cy="124" rx="13" ry="18" fill="#283540" stroke="#1e303e" strokeWidth="1.2"/>
      {/* Microphone — right */}
      <rect x="320" y="138" width="6" height="62" rx="2.5" fill="#8a9399" opacity="0.8"/>
      <rect x="312" y="196" width="26" height="4" rx="2" fill="#8a9399" opacity="0.8"/>
      <ellipse cx="323" cy="124" rx="13" ry="18" fill="#283540" stroke="#1e303e" strokeWidth="1.2"/>
      {/* Recording "on air" light */}
      <circle cx="406" cy="30" r="7" fill="#e03d3d" opacity="0.85"/>
      <circle cx="406" cy="30" r="4" fill="#ff5555" opacity="0.9"/>
      <text x="418" y="34" fontFamily={SANS} fontSize="9.5" fontWeight="700" fill={MUTED}>ON AIR</text>
      {/* Waveform decoration */}
      {[0,1,2,3,4,5,6,7,8,9,10,11,12].map((i) => {
        const heights = [10,18,28,22,34,16,30,20,26,14,22,18,12];
        const h = heights[i];
        return <rect key={i} x={32 + i * 10} y={258 - h / 2} width="4" height={h}
          rx="2" fill={TEAL} opacity="0.28"/>;
      })}
      {/* Gold accent line */}
      <line x1="28" y1="282" x2="180" y2="282" stroke={GOLD} strokeWidth="2" opacity="0.35"/>
    </svg>
  );
}

// ── Waveform bars (player) ───────────────────────────────────────────────────
function Waveform({ played = false }) {
  const bars = [6,14,22,18,28,12,24,16,20,10,18,14,8,12,20,16,24,10,18,14,22,8,16,12,20,18,14,10,16,22];
  return (
    <svg viewBox={`0 0 ${bars.length * 9} 36`} style={{ width: '100%', maxWidth: 240, height: 36 }} aria-hidden="true">
      {bars.map((h, i) => (
        <rect key={i} x={i * 9} y={(36 - h) / 2} width="5" height={h} rx="2.5"
          fill={i < bars.length * 0.35 ? TEAL : BORDER}/>
      ))}
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PodcastPage() {
  const heroImg = useHeroImage('podcast');
  const [playing, setPlaying]       = useState(false);
  const [notified, setNotified]     = useState(false);
  const [email, setEmail]           = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [activeTheme, setTheme]     = useState('');

  return (
    <div style={{ background: PAPER, fontFamily: SANS, color: INK, lineHeight: 1.5, WebkitFontSmoothing: 'antialiased' }} data-testid="podcast-page">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{ borderBottom: `1px solid ${BORDER}`, ...(heroImg && { background: `url(${heroImg}) center/cover no-repeat` }) }}>
        <div className="container-custom" style={{ paddingTop: 52 }}>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.8px', textTransform: 'uppercase', color: GOLD, marginBottom: 18, fontFamily: SANS }}>
                Opportunity Post Podcast
              </div>
              <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 58, lineHeight: 1.02, color: NAVY, margin: '0 0 18px', letterSpacing: '-0.5px' }} className="rt-h1" data-testid="podcast-page-heading">
                The Round Table
              </h1>
              <p style={{ fontFamily: SERIF, fontSize: 22, color: NAVY, fontWeight: 500, margin: '0 0 14px', lineHeight: 1.45 }}>
                Conversations with leaders. Real issues. Practical insights.
              </p>
              <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.7, maxWidth: 460, margin: '0 0 30px' }}>
                The Round Table brings together executives, policymakers, innovators, and frontline leaders to discuss the ideas shaping work, leadership, and opportunity.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <a href="#featured" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: NAVY, color: WHITE, borderRadius: 7, padding: '13px 22px', fontWeight: 700, fontSize: 13.5, textDecoration: 'none', fontFamily: SANS }} data-testid="podcast-listen-cta">
                  <Play size={14} fill={WHITE}/> Listen to Latest Episode
                </a>
                <a href="#subscribe-band" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', color: NAVY, border: `1.5px solid ${BORDER}`, borderRadius: 7, padding: '12px 20px', fontWeight: 600, fontSize: 13.5, textDecoration: 'none', fontFamily: SANS }} data-testid="podcast-alerts-cta">
                  <Bell size={14}/> Get Episode Alerts
                </a>
              </div>
            </div>

            <div style={{ display: 'none', justifyContent: 'center', alignItems: 'flex-end' }}>
              <StudioIllo/>
            </div>
        </div>
      </section>

      <div className="container-custom" style={{ paddingTop: 48, paddingBottom: 60 }}>

        {/* ── Featured Episode ────────────────────────────────────────────── */}
        <div style={{ marginBottom: 48 }} id="featured" data-testid="podcast-featured">
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, marginBottom: 20, fontFamily: SANS }}>
            Featured Episode
          </div>
          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', display: 'grid', gridTemplateColumns: '180px 1fr 200px' }} className="rt-feat-grid">

            {/* Cover art */}
            <div style={{ background: NAVY, padding: '28px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 220 }}>
              <div>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: 6, fontFamily: SANS }}>
                  Opportunity Post
                </div>
                <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 19, color: WHITE, lineHeight: 1.2 }}>The Round Table</div>
              </div>
              {/* Mic icon */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, paddingTop: 12 }}>
                <svg viewBox="0 0 80 80" width="72" height="72" aria-hidden="true">
                  <circle cx="40" cy="40" r="36" fill={WHITE} opacity="0.07"/>
                  <circle cx="40" cy="40" r="26" fill={WHITE} opacity="0.06"/>
                  <rect x="34" y="14" width="12" height="32" rx="6" fill={WHITE} opacity="0.85"/>
                  <path d="M22 38 Q22 56 40 56 Q58 56 58 38" fill="none" stroke={WHITE} strokeWidth="2.2" opacity="0.7"/>
                  <line x1="40" y1="56" x2="40" y2="66" stroke={WHITE} strokeWidth="2.2" opacity="0.7"/>
                  <line x1="30" y1="66" x2="50" y2="66" stroke={WHITE} strokeWidth="2.2" opacity="0.7"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: 'rgba(255,255,255,0.38)', fontFamily: SANS, marginBottom: 4 }}>{FEATURED.season}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', borderRadius: 4, padding: '4px 9px' }}>
                  <Clock size={10} color={GOLD}/>
                  <span style={{ fontSize: 11.5, color: GOLD, fontWeight: 600, fontFamily: SANS }}>33:00</span>
                </div>
              </div>
            </div>

            {/* Info + player — middle column */}
            <div style={{ padding: '28px 28px 24px', borderRight: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: TEAL, marginBottom: 10, fontFamily: SANS }}>
                Featured Episode
              </div>
              <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 24, color: NAVY, margin: '0 0 10px', lineHeight: 1.2 }}>
                {FEATURED.title}
              </h2>
              <p style={{ fontSize: 13, color: TEAL, marginBottom: 20, fontFamily: SANS, fontWeight: 500 }}>
                with {FEATURED.guest}
              </p>

              {/* Player */}
              <div style={{ background: PAPER, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '14px 16px', marginBottom: 16 }} data-testid="podcast-player">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => setPlaying(!playing)}
                    style={{ width: 42, height: 42, borderRadius: '50%', background: NAVY, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    data-testid="podcast-play-btn">
                    {playing
                      ? <svg viewBox="0 0 18 18" width="16" height="16"><rect x="3" y="3" width="4" height="12" rx="1.5" fill={WHITE}/><rect x="11" y="3" width="4" height="12" rx="1.5" fill={WHITE}/></svg>
                      : <Play size={15} fill={WHITE} color={WHITE} style={{ marginLeft: 2 }}/>
                    }
                  </button>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <SkipBack size={14} style={{ color: MUTED, cursor: 'pointer' }}/>
                    <span style={{ fontSize: 9, color: MUTED, fontFamily: SANS, fontWeight: 600 }}>15</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Waveform played={playing}/>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <SkipForward size={14} style={{ color: MUTED, cursor: 'pointer' }}/>
                    <span style={{ fontSize: 9, color: MUTED, fontFamily: SANS, fontWeight: 600 }}>15</span>
                  </div>
                  <span style={{ fontSize: 11.5, color: MUTED, fontFamily: SANS, whiteSpace: 'nowrap', fontWeight: 600, minWidth: 72, textAlign: 'right' }}>
                    {playing ? '0:42' : '0:00'} / 33:00
                  </span>
                </div>
              </div>

              {/* Show Notes / Transcript */}
              <div style={{ display: 'flex', gap: 16 }}>
                {['Show Notes', 'Transcript'].map(l => (
                  <span key={l} style={{ fontSize: 12.5, color: TEAL, fontWeight: 600, cursor: 'pointer', borderBottom: `1.5px solid ${TEAL}`, paddingBottom: 1, fontFamily: SANS }}>{l}</span>
                ))}
              </div>
            </div>

            {/* Listen On — right column */}
            <div style={{ padding: '28px 22px 24px', background: PAPER }} data-testid="podcast-listen-on">
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: NAVY, marginBottom: 16, fontFamily: SANS }}>
                Listen On
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { name: 'Apple Podcasts', href: '#' },
                  { name: 'Spotify', href: '#' },
                  { name: 'YouTube', href: '#' },
                ].map(({ name, href }) => (
                  <a key={name} href={href} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12.5, fontWeight: 600, color: NAVY, textDecoration: 'none', fontFamily: SANS }} data-testid={`podcast-platform-${name.replace(/\s+/g,'-').toLowerCase()}`}>
                    {name} <ChevronRight size={12} style={{ color: MUTED }}/>
                  </a>
                ))}
                <a href="#" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', fontSize: 12.5, fontWeight: 600, color: TEAL, textDecoration: 'none', fontFamily: SANS }} data-testid="podcast-platform-more-platforms">
                  More Platforms <ChevronRight size={12}/>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Next Live Recording ──────────────────────────────────────────── */}
        <div style={{ background: '#edf5f3', border: `1.5px solid #c6ddd9`, borderRadius: 10, padding: '22px 24px', marginBottom: 48, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20 }} data-testid="podcast-live-band">
          <div style={{ width: 44, height: 44, borderRadius: 8, background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Calendar size={20} color={WHITE}/>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 4, fontFamily: SANS }}>Next Live Recording</div>
            <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 18, color: NAVY, marginBottom: 4 }}>
              Public–Private Partnerships for Skills & Opportunity
            </div>
            <div style={{ fontSize: 13, color: MUTED, fontFamily: SANS }}>
              Thursday, June 12, 2025 &nbsp;·&nbsp; 1:00 PM ET
            </div>
          </div>
          <div style={{ fontSize: 13, color: MUTED, maxWidth: 300, fontFamily: SANS, lineHeight: 1.55 }}>
            Join our live round table with leaders from education, industry, and government.
          </div>
          <button
            onClick={() => setNotified(true)}
            style={{ background: notified ? TEAL : NAVY, color: WHITE, border: 'none', borderRadius: 7, padding: '11px 20px', fontWeight: 700, fontSize: 13, cursor: notified ? 'default' : 'pointer', fontFamily: SANS, whiteSpace: 'nowrap', flexShrink: 0 }}
            data-testid="podcast-notify-btn">
            {notified ? 'Reminder set!' : 'Notify Me'}
          </button>
        </div>

        {/* ── Latest Episodes ──────────────────────────────────────────────── */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 26, color: NAVY, margin: 0 }}>Latest Episodes</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }} className="rt-ep-grid" data-testid="podcast-episodes-grid">
            {EPISODES.map(ep => (
              <div key={ep.id} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }} data-testid={`podcast-ep-${ep.id}`}>
                <div style={{ padding: '14px 14px 10px', borderBottom: `1px solid ${BORDER}` }}>
                  <EpIllo theme={ep.theme}/>
                </div>
                <div style={{ padding: '16px 16px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 700, color: THEME_IC[ep.theme] || TEAL, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: SANS }}>
                      {ep.theme}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: MUTED, fontFamily: SANS, fontWeight: 600 }}>
                      <Clock size={9}/> 33:00
                    </span>
                  </div>
                  <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 15.5, color: NAVY, margin: '0 0 6px', lineHeight: 1.3 }}>{ep.title}</h3>
                  <p style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.5, margin: '0 0 12px', fontFamily: SANS }}>{ep.desc}</p>
                  <div style={{ fontSize: 11.5, color: MUTED, fontFamily: SANS }}>{ep.date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Podcast Themes ───────────────────────────────────────────────── */}
        <div style={{ marginBottom: 52 }} data-testid="podcast-themes">
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: MUTED, marginBottom: 14, fontFamily: SANS }}>
            Podcast Themes
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {THEMES.map(t => {
              const active = activeTheme === t;
              return (
                <button key={t} onClick={() => setTheme(active ? '' : t)}
                  style={{ padding: '8px 16px', borderRadius: 20, border: `1.5px solid ${active ? TEAL : BORDER}`, background: active ? '#eaf3f1' : WHITE, color: active ? TEAL : MUTED, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: SANS, transition: 'all .15s' }}
                  data-testid={`podcast-theme-${t.replace(/\s+&?\s*/g,'-').toLowerCase()}`}>
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Partner band ─────────────────────────────────────────────────── */}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '28px 32px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 20, marginBottom: 52 }} data-testid="podcast-partner-band">
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: GOLD, marginBottom: 8, fontFamily: SANS }}>
              Partner with Opportunity Post
            </div>
            <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, color: NAVY, margin: '0 0 6px' }}>
              Reach leaders shaping the future of work, policy, and innovation.
            </h3>
            <p style={{ fontSize: 13.5, color: MUTED, margin: 0, fontFamily: SANS }}>Sponsor placement available for the next recording cycle.</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
            <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: NAVY, color: WHITE, borderRadius: 7, padding: '12px 20px', fontWeight: 700, fontSize: 13, textDecoration: 'none', fontFamily: SANS }} data-testid="podcast-partner-cta">
              Explore Partnerships <ChevronRight size={13}/>
            </Link>
            <Link to="/contact" style={{ fontSize: 12.5, color: TEAL, fontWeight: 600, textDecoration: 'none', textAlign: 'center', fontFamily: SANS }}>
              Contact Us About Sponsorship →
            </Link>
          </div>
        </div>
      </div>

      {/* ── Subscribe band ───────────────────────────────────────────────────── */}
      <section style={{ background: NAVY, padding: '40px 28px' }} id="subscribe-band" data-testid="podcast-subscribe-band">
        <div className="container-custom rt-sub-grid" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 32 }}>
          {/* Left: icon + text */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flex: 1, minWidth: 260 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Mic size={20} color={WHITE} opacity={0.7}/>
            </div>
            <div>
              <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 20, color: WHITE, margin: '0 0 6px', lineHeight: 1.25 }}>
                Get Round Table updates and new episode alerts.
              </h2>
              <p style={{ fontSize: 13, color: '#8aa4b8', margin: 0, fontFamily: SANS }}>Insights delivered to your inbox.</p>
            </div>
          </div>
          {/* Right: form */}
          <div style={{ flexShrink: 0 }}>
            {subscribed ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(29,111,106,0.2)', border: '1.5px solid rgba(29,111,106,0.4)', borderRadius: 8, padding: '13px 20px' }}>
                <svg viewBox="0 0 16 16" width="16" height="16"><circle cx="8" cy="8" r="7" fill={TEAL} opacity="0.8"/><path d="M4.5 8 L7 10.5 L11.5 5.5" fill="none" stroke={WHITE} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <span style={{ color: '#7eb8b4', fontWeight: 600, fontSize: 13.5, fontFamily: SANS }}>You're subscribed — watch your inbox.</span>
              </div>
            ) : (
              <div>
                <form onSubmit={e => { e.preventDefault(); if (email) setSubscribed(true); }} style={{ display: 'flex', gap: 10 }} data-testid="podcast-subscribe-form">
                  <input
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={{ flex: 1, minWidth: 220, border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: 7, padding: '12px 16px', fontSize: 14, background: 'rgba(255,255,255,0.07)', color: WHITE, fontFamily: SANS, outline: 'none' }}
                    data-testid="podcast-email-input"
                  />
                  <button type="submit" style={{ background: GOLD, color: WHITE, border: 'none', borderRadius: 7, padding: '12px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: SANS, whiteSpace: 'nowrap' }} data-testid="podcast-subscribe-btn">
                    Subscribe
                  </button>
                </form>
                <p style={{ fontSize: 11, color: '#5f7b8e', margin: '8px 0 0', fontFamily: SANS }}>
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Footer row ───────────────────────────────────────────────────────── */}
      <section style={{ background: '#0f2538', borderTop: '1px solid rgba(255,255,255,0.05)', padding: '32px 28px' }} data-testid="podcast-footer-row">
        <div className="container-custom rt-foot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, textAlign: 'center' }}>
          {[
            { icon: <Mic size={18} color={GOLD}/>, title: 'Listen Anywhere', sub: 'On your favorite devices' },
            { icon: <Bell size={18} color={GOLD}/>, title: 'Never Miss an Episode', sub: 'Get alerts for new conversations' },
            { icon: <svg viewBox="0 0 18 18" width="18" height="18"><circle cx="9" cy="9" r="7.5" fill="none" stroke={GOLD} strokeWidth="1.5"/><circle cx="9" cy="7" r="2.5" fill={GOLD}/><path d="M4 14.5 Q9 11 14 14.5" fill={GOLD} opacity="0.75"/></svg>, title: 'Be Part of the Conversation', sub: 'Follow. Listen. Share.' },
          ].map(({ icon, title, sub }) => (
            <div key={title} style={{ padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>{icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13.5, color: WHITE, marginBottom: 4, fontFamily: SANS }}>{title}</div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.42)', fontFamily: SANS }}>{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Responsive CSS ───────────────────────────────────────────────────── */}
      <style>{`
        @media (max-width: 900px) {
          .rt-hero-grid  { grid-template-columns: 1fr !important; }
          .rt-feat-grid  { grid-template-columns: 1fr !important; }
          .rt-ep-grid    { grid-template-columns: repeat(2,1fr) !important; }
          .rt-foot-grid  { grid-template-columns: 1fr !important; }
          .rt-h1         { font-size: 42px !important; }
          .rt-sub-grid   { flex-direction: column !important; align-items: flex-start !important; }
        }
        @media (max-width: 600px) {
          .rt-ep-grid    { grid-template-columns: 1fr !important; }
          .rt-h1         { font-size: 34px !important; }
        }
      `}</style>
    </div>
  );
}
